/**
 * The parameters a DataGrid sends with a list request, as pure functions (ortex/ortex-gui#1357).
 *
 * The grid (DataGrid.tsx, `prepareFetchParams` and `fetchData`) builds every page-1 request with
 * `gridFetchParams`, and a page that warms a grid's first request before the grid has mounted (the
 * short-interest hub, src/lib/short-hub-preload.ts) builds it with `firstGridRequest`, which runs the
 * same code with the grid's first-mount state. A warmed request is only used when its parameters are
 * exactly the grid's, so the two must never be computed apart.
 *
 * No React here: the unit tests import it directly.
 */

/** The user setting that holds each grid's saved filters and column order, by tableId. */
export const SAVED_FILTERS_SETTING = "datagrid->saved-filters";

/** DataGrid's default `rowsAmount`. */
export const DEFAULT_ROWS_AMOUNT = 35;

export type GridSortDirection = "asc" | "desc" | null | undefined;

export interface GridSortItem {
    field: string;
    sort: GridSortDirection;
}

/** One grid's entry under SAVED_FILTERS_SETTING. Anything besides these keys is a saved filter. */
export interface SavedGridFilters {
    columnConfiguration?: string[];
    filterSets?: { id?: unknown; customFilterModel?: unknown }[];
    defaultPreset?: unknown;
    [key: string]: unknown;
}

/** A grid's saved filters from the user's settings, or undefined. */
export const savedGridFilters = (moduleSettings: Record<string, any> | undefined | null, tableId: string | undefined): SavedGridFilters | undefined => (tableId ? (moduleSettings?.[SAVED_FILTERS_SETTING]?.[tableId] ?? undefined) : undefined);

/** Rows per page: twice the amount on screens taller or wider than 1920 x 1720. */
export const initialRowsQty = (width: number | false | undefined, height: number | false | undefined, rowsAmount: number = DEFAULT_ROWS_AMOUNT): number => (Number(width || 0) <= 1920 && Number(height || 0) <= 1720 ? rowsAmount : rowsAmount * 2);

export interface GridParamsInput {
    /** True until the grid's first answer has been applied. */
    isFirstMount: boolean;
    /** The grid's `apiParams` state (filters, sort) */
    apiParams: Record<string, any> | undefined;
    sortModel?: readonly GridSortItem[];
    savedFilters?: SavedGridFilters;
    /** The grid's `data` prop. */
    data?: Record<string, any>;
}

export interface GridParams {
    /** Sent as the request's `filter_options`. */
    apiParams: Record<string, any>;
    /** Sent as the request's `data` (undefined: the grid sends `{}`). */
    data: Record<string, any> | string[] | undefined;
    /** The saved column order, when it applies to this request. */
    columnOrder?: string[];
}

/** The filter options and data of a grid request (DataGrid `prepareFetchParams`, without its side effects). */
export const gridFetchParams = ({ isFirstMount, apiParams, sortModel, savedFilters, data }: GridParamsInput): GridParams => {
    const formattedSortModel =
        sortModel?.length && apiParams !== undefined && !Object.prototype.hasOwnProperty.call(apiParams, "sortModel")
            ? {
                  sortModel: sortModel.reduce(
                      (result, { field, sort }) => {
                          result[field] = sort;
                          return result;
                      },
                      {} as Record<string, GridSortDirection>
                  ),
              }
            : undefined;

    let newApiParams: Record<string, any> = {
        ...apiParams,
        ...(isFirstMount ? { initial: true } : formattedSortModel),
    };
    let finalData: GridParams["data"] = data;
    let columnOrder: string[] | undefined;

    // Saved filters from the user's settings
    if (savedFilters) {
        const { columnConfiguration, filterSets, defaultPreset, ...filtersWithoutColumns } = savedFilters;

        if (isFirstMount) {
            const defaultSet = filterSets?.length === 1 ? filterSets[0] : filterSets?.find((s) => s.id === defaultPreset);
            if (defaultSet) newApiParams = { ...apiParams, customFilterModel: defaultSet.customFilterModel };
            else newApiParams = { ...apiParams, ...filtersWithoutColumns };
        }

        if (columnConfiguration && (isFirstMount || !data)) {
            finalData = columnConfiguration;
            columnOrder = columnConfiguration;
        }
    }

    return { apiParams: newApiParams, data: finalData, columnOrder };
};

/** A grid's page-1 request, as `fetchData` passes it to the fetcher (without the id, signal and expand id). */
export interface GridRequest {
    page_size: number;
    page: number;
    filter_options: Record<string, any>;
    data: Record<string, any> | string[];
}

export interface FirstGridRequestInput {
    savedFilters?: SavedGridFilters;
    data?: Record<string, any>;
    /** window.innerWidth / innerHeight when the grid mounts. */
    width: number;
    height: number;
    rowsAmount?: number;
}

/** The first request a grid sends when it mounts, before any filter, sort or paging. */
export const firstGridRequest = ({ savedFilters, data, width, height, rowsAmount }: FirstGridRequestInput): GridRequest => {
    const params = gridFetchParams({ isFirstMount: true, apiParams: {}, sortModel: [], savedFilters, data });
    return { page_size: initialRowsQty(width, height, rowsAmount), page: 1, filter_options: params.apiParams, data: params.data || {} };
};

/** The query string parameters the list controllers send for a grid request. */
export const listQuery = ({ page_size, page, filter_options, data }: { page_size: number; page: number; filter_options?: Record<string, any>; data?: Record<string, any> | string[] }): Record<string, unknown> => ({
    page_size,
    page,
    ...filter_options,
    ...data,
});

/** JSON with object keys sorted, so equal parameters always serialise the same. */
export const stableStringify = (value: unknown): string =>
    JSON.stringify(value, (_key, v) =>
        v && typeof v === "object" && !Array.isArray(v)
            ? Object.keys(v)
                  .sort()
                  .reduce(
                      (out, k) => {
                          out[k] = (v as Record<string, unknown>)[k];
                          return out;
                      },
                      {} as Record<string, unknown>
                  )
            : v
    );

/** The warm-up key of a list request: endpoint plus serialised query. */
export const listWarmKey = (url: string, query: Record<string, unknown>) => `list:${url}?${stableStringify(query)}`;
